import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import {
  computeCityPrices,
  currencyFor,
  findCity,
  formatMoney,
  karatLabel,
  karatPurity,
  KARATS,
  type Country,
  type Karat,
} from "@/lib/gold-data";
import { buildCityFaqs, faqPageSchema, financialProductSchema, liveTimestamp, SITE_URL } from "@/lib/seo";
import { FaqSection } from "@/components/FaqSection";
import { canonicalCityPath } from "@/lib/alias-routes";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { GoldCityTrendChart } from "@/components/GoldCityTrendChart";
import { PremiumGoldCards } from "@/components/PremiumPriceCards";

function todayLabel() {
  return new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export const Route = createFileRoute("/city/$country/$city/$karat")({
  parseParams: (raw: Record<string, string>) => {
    const { country, city, karat } = raw;
    if (country !== "pakistan" && country !== "india") throw notFound();
    const k = karat.toLowerCase();
    if (!KARATS.includes(k as Karat)) throw notFound();
    return { country: country as Country, city, karat: k as Karat };
  },
  loader: ({ context, params }) => {
    if (!findCity(params.country, params.city)) throw notFound();
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  head: ({ params }) => {
    const city = findCity(params.country, params.city);
    const cityName = city?.name ?? "City";
    const kLabel = karatLabel(params.karat);
    const unit = params.country === "pakistan" ? "Tola" : "10 Grams";
    const title =
      params.country === "pakistan"
        ? `Today ${kLabel} Gold Rate in ${cityName} — Live Price per ${unit}`
        : `${kLabel} Gold Price Today in ${cityName} — Live Rate per ${unit}`;
    const description =
      params.country === "pakistan"
        ? `Live ${kLabel} gold rate in ${cityName}, Pakistan today. Price per tola, per gram and per 10g. Updated every 10 minutes from international spot + USD/PKR.`
        : `Today's ${kLabel} gold price in ${cityName}, India. Rate per 10g, per gram and per tola. Updated every 10 minutes.`;
    const path = `/city/${params.country}/${params.city}/${params.karat}`;
    const cityPath = canonicalCityPath(params.country, params.city);
    return {
      meta: [
        { title },
        { name: "robots", content: "noindex,follow" },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: `${SITE_URL}${path}` },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: params.country === "pakistan" ? "en_PK" : "en_IN" },
      ],
      links: [
        // Consolidated: the parent city page already shows 24K/22K/21K/18K
        // side by side, so each karat slice is a facet of that page rather
        // than a separate document. Canonical + noindex points every karat
        // permutation at the one page a reader actually needs.
        { rel: "canonical", href: `${SITE_URL}${cityPath}` },
      ],
    };
  },
  component: KaratCityPage,
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Page not found</h1>
        <p className="mt-2 text-muted-foreground">That city or karat isn't available.</p>
        <div className="mt-4 flex gap-3 justify-center">
          <Link to="/pakistan" className="underline">Pakistan</Link>
          <Link to="/india" className="underline">India</Link>
        </div>
      </div>
    </Layout>
  ),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Could not load rates</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      </div>
    </Layout>
  ),
});

function KaratCityPage() {
  const { country, city: citySlug, karat } = Route.useParams();
  const { data } = useGoldRates();
  const city = findCity(country, citySlug)!;
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const base = computeCityPrices({
    spotUsdPerOz: data.spotUsdPerOz,
    fxRate: fx,
    country,
    cityPremium: city.premium,
  });
  const purity = karatPurity(karat);
  const perGram = base.perGram24 * purity;
  const perTola = base.perTola24 * purity;
  const per10g = base.per10g24 * purity;
  const currency = currencyFor(country);
  const countryName = country === "pakistan" ? "Pakistan" : "India";
  const tz = country === "pakistan" ? "PKT" : "IST";
  const updated = liveTimestamp(data.fetchedAt, tz);
  const kLabel = karatLabel(karat);

  const mainUnit = country === "pakistan" ? "tola" : "10g";
  const mainPrice = country === "pakistan" ? perTola : per10g;
  const productSchema = financialProductSchema({
    name: `${kLabel} Gold Rate in ${city.name}, ${countryName} (per ${mainUnit})`,
    description: `Live ${kLabel} gold rate in ${city.name}, ${countryName} — per gram, per tola and per 10g. Updated every 10 minutes.`,
    price: mainPrice,
    currency,
    url: `${SITE_URL}/city/${country}/${citySlug}/${karat}`,
  });
  const faqs = buildCityFaqs(country, city.name);
  const faqSchema = faqPageSchema(faqs);

  const headlineLabel = country === "pakistan" ? "Per Tola (11.66 g)" : "Per 10 Grams";
  const secondaryLabel = country === "pakistan" ? "Per 10 Grams" : "Per Tola (11.66 g)";
  const secondaryPrice = country === "pakistan" ? per10g : perTola;

  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-4 py-8">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to={country === "pakistan" ? "/pakistan" : "/india"} className="hover:underline">{countryName}</Link> ›{" "}
          <Link to="/city/$country/$city" params={{ country, city: citySlug }} className="hover:underline">{city.name}</Link> › {kLabel}
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold">
          {kLabel} Gold Price in {city.name} Today
        </h1>
        <p className="mt-2 text-muted-foreground">
          Live {kLabel} gold rate for {city.name}, {countryName}. Updated every 10 minutes from international spot + USD/{currency}.
        </p>
        <p className="mt-1 text-xs font-medium text-primary" data-nosnippet>
          <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse mr-2 align-middle" />
          Live Updated: <time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time>
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {KARATS.map((k) => (
            <Link
              key={k}
              to="/city/$country/$city/$karat"
              params={{ country, city: citySlug, karat: k }}
              className={`rounded-full border px-3 py-1 text-xs ${k === karat ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              {karatLabel(k)}
            </Link>
          ))}
        </div>

        <div className="mt-6 -mx-4" aria-label={`${city.name} ${kLabel} gold chart`}>
          <GoldCityTrendChart
            cityName={city.name}
            country={country}
            currentPrice={mainPrice}
            unitLabel={headlineLabel}
            karatLabel={kLabel}
          />
        </div>

        <section className="mt-6 relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-card via-card to-gold/5 p-6 md:p-8 shadow-lg" aria-label={`Live ${kLabel} gold price in ${city.name}`}>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            Live · {kLabel} Gold · {city.name}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">Today {kLabel} Gold Rate — {headlineLabel}</div>
          <div className="mt-2 text-4xl md:text-6xl font-extrabold tracking-tight text-gold leading-none">
            {formatMoney(mainPrice, currency)}
          </div>
          <div className="mt-3 text-sm text-foreground/80">
            <span className="font-semibold">{secondaryLabel}:</span>{" "}
            <span className="font-bold">{formatMoney(secondaryPrice, currency)}</span>
            <span className="mx-2 text-muted-foreground">·</span>
            <span className="font-semibold">Per Gram:</span>{" "}
            <span className="font-bold">{formatMoney(perGram, currency)}</span>
          </div>
          <div className="mt-3 text-xs font-medium text-primary" data-nosnippet>
            Updated: <time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time>
          </div>
        </section>

        <PremiumGoldCards prices={base} currency={currency} regionLabel={`${city.name}, ${countryName}`} />

        <article className="prose prose-sm max-w-none mt-10 text-foreground/90">
          <h2 className="text-xl font-bold">About {kLabel} gold in {city.name}</h2>
          <p className="mt-2 text-muted-foreground">
            {kLabel} gold is {Math.round(purity * 1000) / 10}% pure. The {city.name} rate above is derived from the
            international Sarafa bazar / bullion market spot price, converted to {currency} at today's USD rate, with
            the standard {countryName} local market premium and a small {city.name} adjustment. Jewellers add making
            charges and applicable tax on top — always confirm the day's official rate before you buy.
          </p>
        </article>
        <FaqSection faqs={faqs} title={`${city.name} ${kLabel} gold rate — FAQs`} />
      </section>
    </Layout>
  );
}